/**
 * @file Surface color utilities for Surface Top Bar components
 * Provides consistent color transformations for background, text, and interactive states
 * Refer to SURFACE_COLORS for the color specifications.
 * - Light mode: https://www.figma.com/design/mR7AuhdyT1Qm3cUwv8qYbk/Top-bar?node-id=1740-111169
 * - Dark mode: https://www.figma.com/design/mR7AuhdyT1Qm3cUwv8qYbk/Top-bar?node-id=1374-49624&m=dev
 */

import {
  ColorString,
  CompositeBackgroundString,
  convertRGBToHSL,
  HSLAString,
  HSLString,
  RGBString,
} from '@@/bits/color'

interface SurfaceAccentColors {
  backgroundColor: ColorString | undefined
  textColor: 'white' | 'black' | undefined
}

interface SurfaceAccentColorOptions {
  /** RGB color string of the background (e.g., "rgb(255, 0, 0)") */
  backgroundColorInRgb: RGBString
  /** Accent background color from the theme (used when not custom wallpaper) */
  accentBackgroundColor?: ColorString
  /** Accent text color from the theme (used when not custom wallpaper) */
  accentTextColor?: 'white' | 'black'
  /** Whether the surface uses a custom wallpaper (uploaded image) */
  isCustomWallpaper: boolean
  /** Whether the surface is in map mode */
  isMap: boolean
}

interface SurfaceColors {
  background: {
    default: ColorString
    hover: ColorString
    active: ColorString
  }
  text: {
    default: ColorString
    hover: ColorString
    active: ColorString
  }
  divider: {
    default: ColorString
  }
  searchBar: {
    edit: ColorString
    editText: ColorString
  }
  aiButton: {
    background: CompositeBackgroundString
    text: ColorString
    hoverBackground: CompositeBackgroundString
    hoverText: ColorString
  }
  signupButton: {
    default: ColorString
    hover: ColorString
  }
  buttonInGroup: {
    background: {
      hover: ColorString
    }
  }
  wallTitle?: {
    default: ColorString
  }
  wallDescription?: {
    default: ColorString
  }
  logoText?: {
    default: ColorString
  }
}

interface ColorSpec {
  /** Saturation (0-100). null = use original saturation from background color */
  s: number | null
  /** Lightness (0-100). null = use original lightness from background color */
  l: number | null
  /** Alpha/Opacity (0-1) */
  a: number
}

interface ColorVariant {
  light: ColorSpec
  dark: ColorSpec
}

type ColorCategory = Record<string, ColorVariant>
const SURFACE_COLORS_HSLA: Record<string, ColorCategory> = {
  text: {
    default: {
      light: { s: 70, l: 10, a: 0.9 },
      dark: { s: 70, l: 95, a: 1.0 },
    },
    hover: {
      light: { s: 70, l: 5, a: 1.0 },
      dark: { s: 70, l: 98, a: 1.0 },
    },
    active: {
      light: { s: 70, l: 95, a: 1.0 },
      dark: { s: 70, l: 10, a: 1.0 },
    },
  },

  background: {
    default: {
      light: { s: 70, l: 30, a: 0.1 },
      dark: { s: 70, l: 80, a: 0.1 },
    },
    hover: {
      light: { s: 70, l: 30, a: 0.15 },
      dark: { s: 70, l: 80, a: 0.3 },
    },
    active: {
      light: { s: 70, l: 20, a: 1.0 },
      dark: { s: 70, l: 95, a: 1.0 },
    },
  },

  searchBar: {
    edit: {
      light: { s: null, l: 45, a: 0.25 },
      dark: { s: null, l: 80, a: 0.3 },
    },
  },

  divider: {
    default: {
      light: { s: 70, l: 10, a: 0.2 },
      dark: { s: 70, l: 80, a: 0.3 },
    },
  },

  aiButton: {
    text: {
      light: { s: 70, l: 10, a: 0.95 },
      dark: { s: 70, l: 95, a: 0.9 },
    },
    textHover: {
      light: { s: 15, l: 15, a: 1.0 },
      dark: { s: 15, l: 98, a: 1.0 },
    },
    backgroundTop: {
      light: { s: 70, l: 10, a: 0.1 },
      dark: { s: null, l: null, a: 0.7 },
    },
    backgroundBottom: {
      light: { s: 15, l: 45, a: 0.15 },
      dark: { s: 15, l: 80, a: 0.15 },
    },
    backgroundTopHover: {
      light: { s: null, l: null, a: 0.75 },
      dark: { s: null, l: null, a: 0.7 },
    },
    backgroundBottomHover: {
      light: { s: 15, l: 45, a: 0.15 },
      dark: { s: 15, l: 80, a: 0.25 },
    },
  },

  signupButton: {
    default: {
      light: { s: null, l: 95, a: 1.0 }, // null s = use original saturation
      dark: { s: null, l: 10, a: 1.0 },
    },
    hover: {
      light: { s: null, l: 100, a: 1.0 },
      dark: { s: null, l: 5, a: 1.0 },
    },
  },

  wallTitle: {
    default: {
      light: { s: 70, l: 5, a: 1.0 },
      dark: { s: 70, l: 98, a: 1.0 },
    },
  },

  logoText: {
    default: {
      light: { s: 70, l: 10, a: 0.5 },
      dark: { s: 70, l: 95, a: 0.7 },
    },
  },

  wallDescription: {
    default: {
      light: { s: 70, l: 10, a: 0.7 },
      dark: { s: 70, l: 95, a: 0.7 },
    },
  },
}

interface SurfaceColorOptions {
  /** RGB color string (e.g., "255, 0, 0") */
  backgroundColorInRgb: RGBString
  /** Whether dark mode is active */
  darkMode: boolean
  /** Accent background color to use when isCustomWallpaper is false */
  accentBackgroundColor?: ColorString
  /** Accent text color to use when isCustomWallpaper is false */
  accentTextColor?: 'white' | 'black'
  /** Whether the surface is in map mode */
  isMap?: boolean
  /** Whether to use custom wallpaper color logic (complementary colors). If false, returns accent colors. */
  isCustomWallpaper?: boolean
  /** Whether the title bar is transparent — only applicable for custom wallpapers */
  isTitleBarTransparent?: boolean
}

/**
 * Apply color spec to create HSL color string
 * @param backgroundColorInRgb - Base RGB color
 * @param spec - Color specification
 */
function applyColorSpec(backgroundColorInRgb: RGBString, spec: ColorSpec): HSLString | HSLAString {
  const colorAsHSL = convertRGBToHSL(backgroundColorInRgb)

  const h = colorAsHSL.h
  const s = spec.s ?? colorAsHSL.s
  const l = spec.l ?? colorAsHSL.l

  if (spec.a === 1.0) {
    return `hsl(${h}, ${s}%, ${l}%)`
  }

  return `hsla(${h}, ${s}%, ${l}%, ${spec.a})`
}

/**
 * Get color spec for a given category and state
 * @param category - The category (e.g., 'text', 'background', 'divider')
 * @param state - The state within the category (e.g., 'default', 'hover', 'active')
 * @param darkMode - Whether dark mode is active
 */
function getColorSpec(category: string, state: string, darkMode: boolean): ColorSpec {
  const categorySpec = SURFACE_COLORS_HSLA[category]
  if (!categorySpec) {
    throw new Error(`Unknown color category: ${category}`)
  }

  const stateSpec = categorySpec[state]
  if (!stateSpec) {
    throw new Error(`Unknown state '${state}' for category '${category}'`)
  }

  return darkMode ? stateSpec.dark : stateSpec.light
}

/**
 * Generate background color with original saturation
 * Used for button backgrounds, etc.
 * In map mode, returns OzBoxColors.PrimaryBlur (bg-common-ui-l86, bg-common-ui-d86) for frosted glass effect
 * For custom wallpapers:
 * - default/hover: uses null saturation/lightness with adjusted opacity (preserves original color)
 * - active: uses s=70 (saturated), original spec lightness (l=20 light mode, l=95 dark mode), a=1.0 (solid)
 */
/** Map-mode / no-skin chrome background used when a primary colour is unavailable. */
function getSurfaceFallbackChromeBackground(darkMode: boolean): ColorString {
  return darkMode ? 'rgba(0, 0, 0, 0.86)' : 'rgba(255, 255, 255, 0.86)'
}

/** Map-mode / no-skin chrome text used when a primary colour is unavailable. */
function getSurfaceFallbackChromeText(darkMode: boolean): ColorString {
  return darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.54)'
}

function getSurfaceBackgroundColor(
  options: SurfaceColorOptions,
  state: 'default' | 'hover' | 'active' = 'default',
): HSLAString | ColorString {
  if (options.isMap) {
    return getSurfaceFallbackChromeBackground(options.darkMode)
  }

  const spec = getColorSpec('background', state, options.darkMode)

  if (options.isTitleBarTransparent === false) {
    const customSpec: ColorSpec = {
      s: spec.s,
      l: spec.l !== null ? (options.darkMode ? spec.l + 5 : spec.l - 5) : 20,
      a: spec.a,
    }
    return applyColorSpec(options.backgroundColorInRgb, customSpec) as HSLAString
  }

  if (options.isCustomWallpaper) {
    if (state === 'active') {
      // For active state on custom wallpapers, use saturated (s=70) with spec's lightness
      // This creates a solid, vibrant button with the wallpaper's hue
      const customSpec: ColorSpec = {
        s: 70, // Saturated like preset colors
        l: spec.l, // Keep spec lightness (L=20 for light mode, L=95 for dark mode)
        a: 1.0, // Solid
      }
      return applyColorSpec(options.backgroundColorInRgb, customSpec) as HSLAString
    }

    const baseAlpha = 0.8

    if (state === 'hover') {
      // For non-grouped buttons, simulate the visual of group bg + hover overlay
      // Group bg: original color at 0.8 alpha
      // Hover overlay (button in group): shifted lightness at 0.15 alpha
      // Combined alpha: 0.8 + 0.15*(1-0.8) = 0.83
      // Combined color: 82% original + 18% shifted lightness
      const colorAsHSL = convertRGBToHSL(options.backgroundColorInRgb)
      const shiftedL = options.darkMode ? 70 : 40
      const blendedL = colorAsHSL.l * 0.82 + shiftedL * 0.18

      const customSpec: ColorSpec = {
        s: null,
        l: blendedL,
        a: 0.83,
      }
      return applyColorSpec(options.backgroundColorInRgb, customSpec) as HSLAString
    }

    // Default state
    const customSpec: ColorSpec = {
      s: null,
      l: null,
      a: baseAlpha,
    }

    return applyColorSpec(options.backgroundColorInRgb, customSpec) as HSLAString
  }

  return applyColorSpec(options.backgroundColorInRgb, spec) as HSLAString
}

/**
 * Generate signup button background color
 * Used for signup/login button in Surface Top Bar
 */
function getSurfaceSignupButtonBackground(options: SurfaceColorOptions): HSLAString {
  const spec = getColorSpec('signupButton', 'default', options.darkMode)
  return applyColorSpec(options.backgroundColorInRgb, spec) as HSLAString
}

function getSurfaceSignupButtonBackgroundHover(options: SurfaceColorOptions): HSLAString {
  const spec = getColorSpec('signupButton', 'hover', options.darkMode)
  return applyColorSpec(options.backgroundColorInRgb, spec) as HSLAString
}

/**
 * Generate text/icon color (solid, no opacity)
 * Uses S=15 for consistent desaturated appearance
 * In map mode, returns text-dark-text-200 (light) or text-light-text-200 (dark) to match SurfaceMapControlButtons
 */
function getSurfaceTextColor(options: SurfaceColorOptions): HSLString | ColorString {
  if (options.isMap) {
    return getSurfaceFallbackChromeText(options.darkMode)
  }

  const spec = getColorSpec('text', 'default', options.darkMode)

  return applyColorSpec(options.backgroundColorInRgb, spec) as HSLString
}

/**
 * Generate divider color
 * Uses S=15 with opacity
 */
function getSurfaceDividerColor(options: SurfaceColorOptions): HSLAString {
  const spec = getColorSpec('divider', 'default', options.darkMode)
  return applyColorSpec(options.backgroundColorInRgb, spec) as HSLAString
}

/**
 * Generate hover background color
 * Convenience function for hover states
 * In map mode, returns OzBoxColors.PrimaryBlur (bg-common-ui-l86, bg-common-ui-d86) for frosted glass effect
 */
function getSurfaceHoverBackgroundColor(options: SurfaceColorOptions): HSLAString | ColorString {
  return getSurfaceBackgroundColor(options, 'hover')
}

/**
 * Generate search bar edit mode background color
 * Used for search bar in edit/expanded state
 * In map mode, returns OzBoxColors.PrimaryBlur (bg-common-ui-l86, bg-common-ui-d86) for frosted glass effect
 * For custom wallpapers, uses adjusted alpha and lightness similar to background colors
 */
function getSurfaceSearchBarEditBackgroundColor(options: SurfaceColorOptions): HSLAString | ColorString {
  if (options.isMap) {
    return options.darkMode ? 'rgba(0, 0, 0, 0.86)' : 'rgba(255, 255, 255, 0.86)'
  }

  const spec = getColorSpec('searchBar', 'edit', options.darkMode)

  if (options.isTitleBarTransparent === false) {
    const customSpec: ColorSpec = {
      s: spec.s,
      l: spec.l !== null ? (options.darkMode ? spec.l + 5 : spec.l - 5) : 20,
      a: spec.a,
    }
    return applyColorSpec(options.backgroundColorInRgb, customSpec) as HSLAString
  }

  if (options.isCustomWallpaper) {
    // For custom wallpapers, adjust similar to background colors
    const defaultBgSpec = getColorSpec('background', 'default', options.darkMode)
    const baseAlpha = 0.8

    // Calculate alpha adjustment relative to default background
    const alphaDiff = spec.a - defaultBgSpec.a
    const adjustedAlpha = Math.min(baseAlpha + alphaDiff, 1.0)

    const customSpec: ColorSpec = {
      s: null,
      l: null,
      a: adjustedAlpha,
    }

    return applyColorSpec(options.backgroundColorInRgb, customSpec) as HSLAString
  }

  return applyColorSpec(options.backgroundColorInRgb, spec) as HSLAString
}

/**
 * Generate search bar edit mode text/icon color
 * Used for search bar text and icons in edit/expanded state
 * Uses default text color since edit background is similar to default background
 */
function getSurfaceSearchBarEditTextColor(options: SurfaceColorOptions): HSLString | HSLAString | ColorString {
  // Edit mode uses the same text color as default state
  return getSurfaceTextColor(options)
}

/**
 * Generate AI button background color (default state)
 * Creates a layered background using CSS multiple backgrounds (matching Figma):
 * Format: linear-gradient(0deg, bottomLayer 0%, bottomLayer 100%), topLayer
 * - Top layer: predominant color with 10% opacity (light) or 70% (dark)
 * - Bottom layer: S=15, L=45 (light) or L=80 (dark), opacity 15% (both modes)
 */
function getSurfaceAIButtonBackgroundColor(options: SurfaceColorOptions): CompositeBackgroundString {
  const topSpec = getColorSpec('aiButton', 'backgroundTop', options.darkMode)
  const bottomSpec = getColorSpec('aiButton', 'backgroundBottom', options.darkMode)

  const topLayer = applyColorSpec(options.backgroundColorInRgb, topSpec) as HSLAString
  const bottomLayer = applyColorSpec(options.backgroundColorInRgb, bottomSpec) as HSLAString

  // Combine both layers using Figma's format: linear-gradient for bottom, then top layer
  return `linear-gradient(0deg, ${bottomLayer} 0%, ${bottomLayer} 100%), ${topLayer}` as CompositeBackgroundString
}

/**
 * Generate AI button background color (hover state)
 * Creates a layered background using CSS multiple backgrounds (matching Figma):
 * Format: linear-gradient(0deg, bottomLayer 0%, bottomLayer 100%), topLayer
 * - Top layer: predominant color with 75% opacity (light) or 70% (dark)
 * - Bottom layer: S=15, L=45 (light) or L=80 (dark), opacity 15% (light) or 25% (dark)
 */
function getSurfaceAIButtonBackgroundHoverColor(options: SurfaceColorOptions): CompositeBackgroundString {
  const topSpec = getColorSpec('aiButton', 'backgroundTopHover', options.darkMode)
  const bottomSpec = getColorSpec('aiButton', 'backgroundBottomHover', options.darkMode)

  const topLayer = applyColorSpec(options.backgroundColorInRgb, topSpec) as HSLAString
  const bottomLayer = applyColorSpec(options.backgroundColorInRgb, bottomSpec) as HSLAString

  // Combine both layers using Figma's format: linear-gradient for bottom, then top layer
  return `linear-gradient(0deg, ${bottomLayer} 0%, ${bottomLayer} 100%), ${topLayer}` as CompositeBackgroundString
}

/**
 * Generate AI button text/icon color (default state)
 * Uses S=70 with 95% opacity (light) or 90% (dark)
 * Light mode: L=10 (darker text), Dark mode: L=95 (lighter text)
 */
function getSurfaceAIButtonTextColor(options: SurfaceColorOptions): HSLAString {
  const spec = getColorSpec('aiButton', 'text', options.darkMode)
  return applyColorSpec(options.backgroundColorInRgb, spec) as HSLAString
}

/**
 * Generate AI button text/icon color (hover state)
 * Uses S=15 with 100% opacity (solid color)
 * Light mode: L=15 (darker text), Dark mode: L=98 (lighter text)
 */
function getSurfaceAIButtonTextHoverColor(options: SurfaceColorOptions): HSLAString {
  const spec = getColorSpec('aiButton', 'textHover', options.darkMode)
  return applyColorSpec(options.backgroundColorInRgb, spec) as HSLAString
}

/**
 * Bundle function that returns all AI button colors at once.
 */
function getSurfaceAIButtonColors(options: SurfaceColorOptions): {
  background: CompositeBackgroundString
  text: HSLAString
  hoverBackground: CompositeBackgroundString
  hoverText: HSLAString
} {
  return {
    background: getSurfaceAIButtonBackgroundColor(options),
    text: getSurfaceAIButtonTextColor(options),
    hoverBackground: getSurfaceAIButtonBackgroundHoverColor(options),
    hoverText: getSurfaceAIButtonTextHoverColor(options),
  }
}

/**
 * Generate hover text color
 * For map mode: Returns color values matching SurfaceMapControlButtons hover behavior
 * For non-map mode: Uses S=15 with darker/lighter lightness for hover state
 * Light mode: S=15, L=15 (darker) or #111111 (text-dark-text-100) for map
 * Dark mode: S=15, L=85 (lighter) or #FFFFFF (text-light-text-100) for map
 */
function getSurfaceHoverTextColor(options: SurfaceColorOptions): ColorString {
  if (options.isMap) {
    return options.darkMode ? '#FFFFFF' : '#111111'
  }

  const spec = getColorSpec('text', 'hover', options.darkMode)

  return applyColorSpec(options.backgroundColorInRgb, spec) as HSLString
}

/**
 * Generate active background color (solid, no opacity)
 * Uses the default text color as the background
 * Based on S=15, L=45 (light) or L=80 (dark)
 */
function getSurfaceBackgroundActiveColor(options: SurfaceColorOptions): HSLString | HSLAString | ColorString {
  return getSurfaceBackgroundColor(options, 'active')
}

/**
 * Generate active text color
 * Uses predominant color with S=70 and swapped lightness for contrast
 * Light mode: L=95 (very light), Dark mode: L=10 (darker)
 * For custom wallpapers: uses s=70 (saturated) with spec lightness to create vibrant contrast against active background
 * For map mode: uses brand colors (grape-500 / canary-500) to match SurfaceMapControlButtons
 */
function getSurfaceTextActiveColor(options: SurfaceColorOptions): HSLString | ColorString {
  if (options.isMap) {
    // Match SurfaceMapControlButtons active state: text-grape-500 (light) / text-canary-500 (dark)
    return options.darkMode ? '#FEC006' : '#9466E8'
  }

  const spec = getColorSpec('text', 'active', options.darkMode)

  if (options.isCustomWallpaper) {
    // For custom wallpapers, use saturated (s=70) with spec's lightness
    // This creates vibrant text that contrasts with the active background
    // Light mode: L=95 (light text on dark background)
    // Dark mode: L=10 (dark text on light background)
    const customSpec: ColorSpec = {
      s: 70, // Saturated like preset colors
      l: spec.l, // Keep spec lightness (L=95 for light mode, L=10 for dark mode)
      a: spec.a,
    }
    return applyColorSpec(options.backgroundColorInRgb, customSpec) as HSLString
  }

  return applyColorSpec(options.backgroundColorInRgb, spec) as HSLString
}

/**
 * Generate wall title color
 * Uses S=70 with distinct lightness values
 * Light mode: L=15 (darker), Dark mode: L=98 (very light)
 */
function getSurfaceWallTitleColor(options: SurfaceColorOptions): HSLString | HSLAString {
  const spec = getColorSpec('wallTitle', 'default', options.darkMode)
  return applyColorSpec(options.backgroundColorInRgb, spec) as HSLString | HSLAString
}

/**
 * Generate logo text color
 * Uses S=70 with 70% opacity
 * Light mode: L=20 (darker), Dark mode: L=95 (lighter)
 */
function getSurfaceLogoTextColor(options: SurfaceColorOptions): HSLAString {
  const spec = getColorSpec('logoText', 'default', options.darkMode)
  return applyColorSpec(options.backgroundColorInRgb, spec) as HSLAString
}

/**
 * Generate wall description & builder text color
 * Uses S=70 with 70% opacity
 * Light mode: L=20 (darker), Dark mode: L=95 (lighter)
 */
function getSurfaceWallDescriptionColor(options: SurfaceColorOptions): HSLAString {
  const spec = getColorSpec('wallDescription', 'default', options.darkMode)
  return applyColorSpec(options.backgroundColorInRgb, spec) as HSLAString
}

/**
 * Generate button in group hover background color (more transparent than default)
 * Used for buttons within button groups to create visual hierarchy
 * Alpha is computed as: background.hover.a - background.default.a
 * Light mode: S=55, L=10, a=0.05 (0.15 - 0.1)
 * Dark mode: S=null (original), L=80, a=0.2 (0.3 - 0.1)
 * For custom wallpapers, uses null s/l to preserve original color with adjusted opacity
 */
function getSurfaceButtonInGroupHoverBackgroundColor(options: SurfaceColorOptions): HSLAString {
  const hoverSpec = getColorSpec('background', 'hover', options.darkMode)
  const defaultSpec = getColorSpec('background', 'default', options.darkMode)
  const alphaDiff = hoverSpec.a - defaultSpec.a

  if (options.isTitleBarTransparent === false) {
    const spec: ColorSpec = {
      s: hoverSpec.s,
      l: hoverSpec.l !== null ? (options.darkMode ? hoverSpec.l + 5 : hoverSpec.l - 5) : 20,
      a: alphaDiff,
    }
    return applyColorSpec(options.backgroundColorInRgb, spec) as HSLAString
  }

  if (options.isCustomWallpaper) {
    const spec: ColorSpec = {
      s: null,
      l: options.darkMode ? 70 : 40,
      a: 0.15,
    }
    return applyColorSpec(options.backgroundColorInRgb, spec) as HSLAString
  }

  // Create spec with computed alpha, using hover's s/l values
  const spec: ColorSpec = {
    s: hoverSpec.s,
    l: hoverSpec.l,
    a: alphaDiff,
  }

  return applyColorSpec(options.backgroundColorInRgb, spec) as HSLAString
}

/**
 * Get the Surface accent pair (background + text) based on surface type.
 * Shared by accent controls (e.g. Add-post, view-as preview banner) — not Add-post-only.
 * For custom wallpapers, dynamically imports color matching logic.
 * For maps and preset themes, uses theme accent colors directly.
 *
 * @param options - Color options including background color and surface type
 * @returns Surface accent pair (background and text)
 */
async function getSurfaceAccentColors(options: SurfaceAccentColorOptions): Promise<SurfaceAccentColors> {
  const { backgroundColorInRgb, isCustomWallpaper, isMap, accentBackgroundColor, accentTextColor } = options

  if (isMap || !isCustomWallpaper) {
    return {
      backgroundColor: accentBackgroundColor,
      textColor: accentTextColor,
    }
  }

  // For custom wallpapers, dynamically import the color matching logic
  const { getCustomWallpaperAccentColors } = await import('@@/bits/add_post_button_color')
  return getCustomWallpaperAccentColors(backgroundColorInRgb)
}
export {
  getSurfaceFallbackChromeBackground,
  getSurfaceFallbackChromeText,
  getSurfaceBackgroundColor,
  getSurfaceSignupButtonBackground,
  getSurfaceSignupButtonBackgroundHover,
  getSurfaceTextColor,
  getSurfaceDividerColor,
  getSurfaceHoverBackgroundColor,
  getSurfaceSearchBarEditBackgroundColor,
  getSurfaceSearchBarEditTextColor,
  getSurfaceAIButtonBackgroundColor,
  getSurfaceAIButtonBackgroundHoverColor,
  getSurfaceAIButtonTextColor,
  getSurfaceAIButtonTextHoverColor,
  getSurfaceAIButtonColors,
  getSurfaceHoverTextColor,
  getSurfaceBackgroundActiveColor,
  getSurfaceTextActiveColor,
  getSurfaceWallTitleColor,
  getSurfaceLogoTextColor,
  getSurfaceWallDescriptionColor,
  getSurfaceButtonInGroupHoverBackgroundColor,
  getSurfaceAccentColors,
}
export type { SurfaceAccentColors, SurfaceAccentColorOptions, SurfaceColors }
